import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { eventsAPI } from '../../api';
import { useAuth } from '../../context/AuthContext';
import toast from 'react-hot-toast';

export default function DashboardPage() {
  const { organizer, logout } = useAuth();
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    eventsAPI.myEvents().then(r => setEvents(r.data)).finally(() => setLoading(false));
  }, []);

  const handleLogout = () => { logout(); navigate('/organizer/login'); };

  const statusColor = { draft: '#9CA3AF', published: '#2D7D32', cancelled: '#DC2626', completed: '#1A2744' };
  const statusBg = { draft: '#f3f4f6', published: '#f0fdf4', cancelled: '#fef2f2', completed: '#f0f4ff' };

  return (
    <div style={{ minHeight: '100vh', background: '#f8f9fa' }}>
      {/* Header */}
      <div style={{ background: '#1A2744', padding: '16px 24px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <img src="https://omogusiiwarriors.org/icons/logo.png" alt="OWW" style={{ width: 36, height: 36, objectFit: 'contain' }} />
          <div>
            <div style={{ color: '#fff', fontWeight: 700, fontFamily: "'Playfair Display', serif" }}>Tikoo Dashboard</div>
            <div style={{ color: 'rgba(255,255,255,0.5)', fontSize: 12 }}>{organizer?.name} {organizer?.organization ? `· ${organizer.organization}` : ''}</div>
          </div>
        </div>
        <div style={{ display: 'flex', gap: 12 }}>
          <Link to="/" style={{ color: 'rgba(255,255,255,0.6)', fontSize: 13, padding: '8px 12px' }}>Public Site</Link>
          <button onClick={handleLogout} style={{ background: 'rgba(255,255,255,0.1)', color: '#fff', border: 'none', padding: '8px 16px', borderRadius: 8, cursor: 'pointer', fontSize: 13 }}>Logout</button>
        </div>
      </div>

      <div style={{ maxWidth: 1000, margin: '0 auto', padding: '32px 24px' }}>
        {/* Stats */}
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(160px,1fr))', gap: 16, marginBottom: 32 }}>
          {[
            { label: 'Total Events', value: events.length, icon: '🎭' },
            { label: 'Published', value: events.filter(e => e.status === 'published').length, icon: '✅' },
            { label: 'Tickets Sold', value: events.reduce((s, e) => s + (e.tickets_sold || 0), 0), icon: '🎟️' },
            { label: 'Revenue', value: `KSh ${events.reduce((s, e) => s + (e.revenue || 0), 0).toLocaleString()}`, icon: '💰' },
          ].map(s => (
            <div key={s.label} style={{ background: '#fff', borderRadius: 12, padding: 20, textAlign: 'center', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
              <div style={{ fontSize: 28, marginBottom: 6 }}>{s.icon}</div>
              <div style={{ fontSize: 22, fontWeight: 800, color: '#1A2744' }}>{s.value}</div>
              <div style={{ fontSize: 12, color: '#9CA3AF' }}>{s.label}</div>
            </div>
          ))}
        </div>

        {/* Events */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
          <h2 style={{ fontFamily: "'Playfair Display', serif", color: '#1A2744', margin: 0 }}>My Events</h2>
          <div style={{ display: 'flex', gap: 10 }}>
            <Link to="/organizer/payouts"
              style={{ background: '#f3f4f6', color: '#374151', padding: '10px 20px', borderRadius: 8, fontWeight: 700, fontSize: 14 }}>
              💰 Earnings
            </Link>
            <Link to="/organizer/events/new"
              style={{ background: '#E8521A', color: '#fff', padding: '10px 20px', borderRadius: 8, fontWeight: 700, fontSize: 14 }}>
              + Create Event
            </Link>
          </div>
        </div>

        {loading ? <div style={{ textAlign: 'center', padding: 40, color: '#9CA3AF' }}>Loading...</div>
        : events.length === 0 ? (
          <div style={{ background: '#fff', borderRadius: 12, padding: 48, textAlign: 'center' }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>🎭</div>
            <h3 style={{ color: '#374151' }}>No events yet</h3>
            <p style={{ color: '#9CA3AF', marginBottom: 20 }}>Create your first event and start selling tickets!</p>
            <Link to="/organizer/events/new"
              style={{ background: '#E8521A', color: '#fff', padding: '12px 24px', borderRadius: 8, fontWeight: 700 }}>
              + Create Event
            </Link>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {events.map(event => (
              <div key={event.id} style={{ background: '#fff', borderRadius: 12, padding: 20, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12, boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
                <div style={{ flex: 1 }}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
                    <span style={{ fontWeight: 700, color: '#1A2744', fontSize: 16 }}>{event.name}</span>
                    <span style={{ background: statusBg[event.status], color: statusColor[event.status], padding: '2px 10px', borderRadius: 20, fontSize: 12, fontWeight: 600, textTransform: 'capitalize' }}>{event.status}</span>
                  </div>
                  <div style={{ fontSize: 13, color: '#6B7280' }}>📅 {new Date(event.date).toLocaleDateString('en-KE', { dateStyle: 'medium' })} · 📍 {event.venue}</div>
                  <div style={{ fontSize: 13, color: '#9CA3AF', marginTop: 4 }}>🎟️ {event.tickets_sold || 0} sold · 💰 KSh {(event.revenue || 0).toLocaleString()}</div>
                </div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <Link to={`/event/${event.id}`}
                    style={{ padding: '8px 14px', background: '#f3f4f6', color: '#374151', borderRadius: 6, fontSize: 13, fontWeight: 600 }}>
                    View
                  </Link>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
